/**
 * Home screen. A hero band, a launch panel (pick the current topic then jump
 * into Practice / Quiz / Mixed Review), and the three units laid out as
 * distinct, color-accented sections listing their topics.
 */

import type { Screen } from "../router";
import { el } from "../../ui/dom";
import {
  UNITS,
  TOPICS,
  getTopic,
  topicsInUnit,
  type TopicId,
  type UnitId,
} from "../../curriculum/curriculum";
import { hasGenerator } from "../../topics/registry";
import { createTopicSelect } from "../../ui/topic-select";
import { getProgressRepository } from "../../progress/repository";
import { recommendedTopics } from "../question-select";
import { suggestNext, type Suggestion, type TopicStatus } from "../suggest";
import type { Store } from "../state";

/** Per-unit accent hue so the three units read as distinct sections. */
const UNIT_ACCENT: Record<UnitId, string> = {
  unit1: "#1f6bff",
  unit2: "#7a5cff",
  unit3: "#00b3a4",
  unit4: "#e8853a",
};

export const homeScreen: Screen = (store, mount) => {
  const state = store.get();
  const taught = store.taughtSet();
  const current = getTopic(state.settings.currentTopic);

  // Hero band.
  mount.appendChild(
    el(
      "section",
      { class: "hero" },
      el("div", { class: "hero-glow", "aria-hidden": "true" }),
      el("img", {
        class: "hero-logo",
        src: "/axle-logo.png",
        alt: "AXLE",
        width: 300,
        height: 200,
      }),
      el(
        "p",
        { class: "hero-tagline" },
        "Momentum for Algebra I",
      ),
    ),
  );

  // Launch panel: choose the topic (grouped by unit), then act on it.
  const topicSelect = createTopicSelect({
    selected: current.id,
    ariaLabel: "Current topic",
    onChange: (t) => store.setCurrentTopic(t),
  });

  mount.appendChild(
    el(
      "section",
      { class: "launch card" },
      el("p", { class: "card-title" }, "Current Topic"),
      topicSelect,
      el(
        "div",
        { class: "launch-actions" },
        actionTile(
          "Learn",
          "Read the concept, method, and a worked example for this topic.",
          "\uD83D\uDCD8",
          "cyan",
          () =>
            store.navigate("learn", {
              topic: store.get().settings.currentTopic,
            }),
        ),
        actionTile(
          "Practice",
          "Work problems one at a time with hints and full solutions.",
          "\u270E",
          "primary",
          () =>
            store.navigate("practice", {
              topic: store.get().settings.currentTopic,
            }),
        ),
        actionTile(
          "Quiz",
          "Timed-feel set with scoring and a missed-question review.",
          "\u2714",
          "",
          () => store.navigate("quiz"),
        ),
        actionTile(
          "Mixed Review",
          "Questions drawn from across every topic you have covered.",
          "\u21BB",
          "",
          () => store.navigate("quiz"),
        ),
      ),
    ),
  );

  // A quiet "suggested next" nudge, if the data suggests one.
  renderSuggestion(store, mount);

  // Units.
  for (const unit of UNITS) {
    const accent = UNIT_ACCENT[unit.id];
    const topics = topicsInUnit(unit.id);
    const readyCount = topics.filter((t) => hasGenerator(t.id)).length;

    const list = el("ul", { class: "topic-list" });
    for (const topic of topicsInUnit(unit.id)) {
      list.appendChild(renderTopicRow(store, topic.id, taught.has(topic.id)));
    }

    mount.appendChild(
      el(
        "section",
        { class: "card unit-card", style: `--unit-accent:${accent}` },
        el(
          "div",
          { class: "unit-head" },
          el("span", { class: "unit-num" }, String(unit.order)),
          el(
            "div",
            {},
            el("p", { class: "unit-name" }, unit.name),
            el(
              "p",
              { class: "unit-sub" },
              `${topics.length} topics \u00b7 ${readyCount} ready`,
            ),
          ),
        ),
        list,
      ),
    );
  }
};

function actionTile(
  title: string,
  desc: string,
  icon: string,
  variant: "primary" | "cyan" | "",
  onClick: () => void,
): HTMLElement {
  return el(
    "button",
    { class: `action-tile ${variant ? "action-tile-" + variant : ""}`, onClick },
    el("span", { class: "action-icon", "aria-hidden": "true" }, icon),
    el(
      "span",
      { class: "action-text" },
      el("span", { class: "action-title" }, title),
      el("span", { class: "action-desc" }, desc),
    ),
  );
}

/** Build the suggestion input from progress data and render a nudge, if any. */
function renderSuggestion(store: Store, mount: HTMLElement): void {
  const repo = getProgressRepository();
  if (!repo) return;
  const taught = store.taughtSet();

  const topics: TopicStatus[] = TOPICS.filter(
    (t) => hasGenerator(t.id) && taught.has(t.id),
  ).map((t) => ({
    topic: t.id,
    mastery: repo.mastery(t.id),
    lastPracticedAt: repo.raw().topics[t.id]?.lastPracticedAt ?? null,
  }));

  const suggestion = suggestNext({
    now: Date.now(),
    topics,
    concernCount: repo.concerns().length,
    sessionCount: repo.sessions().length,
  });
  if (!suggestion) return;

  mount.appendChild(
    el(
      "section",
      { class: "card suggestion" },
      el("span", { class: "suggestion-badge" }, "Suggested next"),
      el("p", { class: "suggestion-text" }, suggestion.message),
      el(
        "div",
        { class: "btn-row", style: "margin-top:0.5rem" },
        el(
          "button",
          {
            class: "btn btn-primary",
            onClick: () => runSuggestion(store, suggestion),
          },
          suggestion.cta,
        ),
      ),
    ),
  );
}

/** Route a suggestion's action to the right screen. */
function runSuggestion(store: Store, s: Suggestion): void {
  switch (s.action.kind) {
    case "practice":
      store.navigate("practice", { topic: s.action.topic });
      break;
    case "learn":
      store.navigate("learn", { topic: s.action.topic });
      break;
    case "recommended": {
      const repo = getProgressRepository();
      const taught = store.taughtSet();
      const topics = repo
        ? recommendedTopics(repo.concerns().map((c) => c.subskill), taught)
        : [];
      if (topics.length > 0) {
        store.navigate("quiz", {
          autostart: {
            scope: { kind: "recommended", topics },
            difficulty: "standard",
            count: 8,
          },
        });
      } else {
        store.navigate("quiz");
      }
      break;
    }
  }
}

function renderTopicRow(
  store: Parameters<Screen>[0],
  topicId: TopicId,
  isTaught: boolean,
): HTMLElement {
  const topic = getTopic(topicId);
  const ready = hasGenerator(topicId);

  const badgeText = !ready
    ? "Coming soon"
    : isTaught
      ? "Ready"
      : "Not yet taught";
  const badgeClass = ready && isTaught ? "badge badge-ready" : "badge";
  const badge = el("span", { class: badgeClass }, badgeText);

  const nameBtn = el(
    "button",
    {
      class: "topic-link nav-link",
      disabled: !ready,
      onClick: () => {
        if (ready) store.navigate("practice", { topic: topicId });
      },
    },
    topic.name,
  );

  return el("li", { class: "topic-item" }, nameBtn, badge);
}
